import { Tooltip } from 'antd';
import classNames from 'classnames';
import React from 'react';

import s from './styles.module.scss';

import type {
  IHeaderQuickActionBadge,
  IHeaderQuickActionItem,
  IHeaderQuickActionsProps,
} from './types';

const clampValue = (value?: number, max: number = 99) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return Math.min(max, Math.max(0, value));
};

const HeaderQuickActions: React.FC<IHeaderQuickActionsProps> = ({
  actions,
  className,
  size = 'medium',
  ariaLabel = 'Quick actions',
}) => {
  if (!actions?.length) return null;

  const renderBadge = (badge?: IHeaderQuickActionBadge) => {
    if (!badge || badge.variant === undefined) return null;
    const toneClass = badge.tone ? s[`badge-tone-${badge.tone}`] : undefined;

    if (badge.variant === 'dot') {
      return (
        <span className={classNames(s.badge, s['badge-dot'], toneClass)} />
      );
    }

    const value = clampValue(badge.value, badge.max ?? 99);
    if (badge.variant === 'count' && typeof value === 'number') {
      return (
        <span className={classNames(s.badge, s['badge-count'], toneClass)}>
          {value}
        </span>
      );
    }

    return null;
  };

  const renderActionButton = (action: IHeaderQuickActionItem) => {
    const button = (
      <button
        type="button"
        onClick={action.onClick}
        aria-label={action.ariaLabel}
        aria-pressed={action.isToggle ? Boolean(action.isActive) : undefined}
        disabled={action.disabled}
        data-cy={action.dataCy}
        data-testid={action.testId}
        className={classNames(
          s['quick-action-button'],
          s[`quick-action-button-${size}`],
          {
            [s['quick-action-button-active']]: action.isActive,
            [s['quick-action-button-disabled']]: action.disabled,
          },
        )}
      >
        <span className={s['quick-action-icon']}>{action.icon}</span>
        {renderBadge(action.badge)}
      </button>
    );

    if (action.tooltip) {
      return (
        <Tooltip key={action.id} title={action.tooltip} placement="bottom">
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  return (
    <div
      className={classNames(s['quick-actions'], className)}
      role="group"
      aria-label={ariaLabel}
    >
      {actions.map((action) => (
        <React.Fragment key={action.id}>
          {renderActionButton(action)}
        </React.Fragment>
      ))}
    </div>
  );
};

export default HeaderQuickActions;

export type {
  IHeaderQuickActionBadge,
  IHeaderQuickActionItem,
  IHeaderQuickActionsProps,
} from './types';
